import React from 'react';
import { Box, Card, CardContent, CardHeader, Divider } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useMotionPresets } from '../../theme/motion';

export default function SectionCard({
  title,
  subheader,
  action,
  children,
  disablePadding = false,
  sx,
  contentSx,
  tone = 'default',
  ...rest
}) {
  const motionPresets = useMotionPresets();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const isDashboard = tone === 'dashboard';

  const cardSx = isDashboard
    ? {
        borderRadius: theme.hr?.radius?.lg || 3,
        border: '1px solid',
        borderColor: theme.hr?.line?.soft || theme.palette.divider,
        backgroundColor:
          theme.hr?.surface?.glass || alpha(theme.palette.background.paper, dark ? 0.78 : 0.92),
        backdropFilter: 'blur(12px)',
        boxShadow: theme.hr?.shadow?.soft || theme.shadows[2],
      }
    : null;

  const headerSx = isDashboard
    ? {
        py: 1.5,
        px: { xs: 1.75, sm: 2.1 },
        '& .MuiCardHeader-action': { alignSelf: 'center', mt: 0 },
      }
    : { pb: 1.25 };

  const headerTitleProps =
    isDashboard && typeof title === 'string'
      ? { variant: 'subtitle1', sx: { fontWeight: 850 } }
      : undefined;

  const headerSubheaderProps =
    isDashboard && typeof subheader === 'string'
      ? { variant: 'caption', sx: { mt: 0.25 }, color: 'text.secondary' }
      : undefined;

  const resolvedContentSx = disablePadding
    ? { p: 0, ...contentSx }
    : isDashboard
      ? [{ p: { xs: 1.75, sm: 2.1 } }, contentSx].filter(Boolean)
      : contentSx;

  return (
    <Card
      component={motion.div}
      {...motionPresets.scaleIn}
      sx={[cardSx, sx].filter(Boolean)}
      {...rest}
    >
      {title ? (
        <>
          <CardHeader
            title={title}
            subheader={subheader}
            action={action}
            sx={headerSx}
            titleTypographyProps={headerTitleProps}
            subheaderTypographyProps={headerSubheaderProps}
          />
          <Divider />
        </>
      ) : null}
      <CardContent sx={resolvedContentSx}>
        <Box>{children}</Box>
      </CardContent>
    </Card>
  );
}

